import * as Accordion from '@radix-ui/react-accordion';
import cn from 'classnames';

import NoSSR from 'components/noSSR';
import { CaretDown } from 'components/svg/CaretDown';
import { useAppSelector } from 'hooks/useRedux';
import { useGetResumeDataQuery } from 'store/apis/user';

import { CAREER_LINKS, COMMON_LINKS, COMPANIES_LINKS, JOBS_LINKS } from './data';
import { CommonList, IconList, ListProps, TitleList } from './links';
import { mobileNavContext } from './mobileNav';

import classes from './index.module.css';

export interface MobileNavigationProps {
  getCommonLinks(options: unknown): ListProps['links'];
  onLinkClick(): void;
}

const getList = (list: ListProps) => {
  if (list?.title) {
    return <TitleList title={list.title} links={list.links} />;
  }

  if (list?.iconList) {
    return <IconList links={list.links} />;
  }

  return <CommonList links={list.links} />;
};

const Trigger = ({ children, className, ...props }: Accordion.AccordionTriggerProps) => (
  <Accordion.Header className="z-m-0" asChild>
    <Accordion.Trigger
      className={cn(
        'z-font-base z-leading-base w-100 d-flex justify-content-between align-items-center z-px-15 z-py-18',
        classes.mobileTrigger
      )}
      {...props}
    >
      {children}
      <CaretDown className={cn('z-ml-6', classes.caretDown)} />
    </Accordion.Trigger>
  </Accordion.Header>
);

const Content = ({ children, className, ...props }: Accordion.AccordionContentProps) => (
  <Accordion.Content className={cn('overflow-hidden', classes.mobileContent, className)} {...props}>
    <div className={cn('z-mx-15 z-py-24 d-flex flex-column', classes.mobileContentChild)}>
      {children}
    </div>
  </Accordion.Content>
);

export const MobileNavigation = ({ getCommonLinks, onLinkClick }: MobileNavigationProps) => {
  const isLoggedIn = useAppSelector(({ user }) => user.isLoggedIn);
  const { data: resumeData } = useGetResumeDataQuery('resumeUser', { skip: !isLoggedIn });
  const commonLinks = getCommonLinks({ isCompleted: resumeData?.isCompleted ?? false });

  return (
    <mobileNavContext.Provider value={{ onLinkClick }}>
      <Accordion.Root type="single" collapsible>
        {isLoggedIn ? (
          <div className={classes.accordionItem}>
            <div className="z-mx-15 z-py-18">
              <CommonList links={[{
                text: 'Jobs',
                href: '/matches/',
              }]} />
            </div>
          </div>
        ) : (
          <Accordion.Item className={classes.accordionItem} value="jobs">
            <Trigger>Jobs</Trigger>
            <Content>{JOBS_LINKS.slice(0, 2).map(getList)}</Content>
          </Accordion.Item>
        )}
        <Accordion.Item className={classes.accordionItem} value="career">
          <Trigger>Career</Trigger>
          <Content>{CAREER_LINKS.slice(0, 2).map(getList)}</Content>
        </Accordion.Item>
        <Accordion.Item className={classes.accordionItem} value="companies">
          <Trigger>Companies</Trigger>
          <Content>{COMPANIES_LINKS.slice(0, 2).map(getList)}</Content>
        </Accordion.Item>
        <NoSSR>
          <div className="z-mx-15 z-py-18">
            {getList({ links: commonLinks?.length ? commonLinks : COMMON_LINKS({ isLoggedIn: false }) })}
          </div>
        </NoSSR>
      </Accordion.Root>
    </mobileNavContext.Provider>
  );
};
